
import { memo, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBuyer } from "@/contexts/BuyerContext";

interface LogoProps {
  size?: "small" | "medium" | "large";
  className?: string;
  context?: 'buyer' | 'seller' | 'public';
  disableLink?: boolean;
}

const Logo = memo(({ size = "medium", className = "", context, disableLink = false }: LogoProps) => {
  const { user, profile, isLoading } = useAuth();
  const { buyerProfile, isLoading: buyerLoading } = useBuyer();
  const location = useLocation();
  
  const sizeClasses = {
    small: "text-xl",
    medium: "text-2xl",
    large: "text-4xl"
  };

  // Memoize the link destination calculation
  const linkDestination = useMemo(() => {
    // Check if we're on authentication pages (login, register, forgot password, reset password)
    const isOnAuthPage = location.pathname.includes('/login') || 
                        location.pathname.includes('/register') || 
                        location.pathname.includes('/forgot-password') || 
                        location.pathname.includes('/reset-password');

    // Check if we're currently in a loading state (during logout or login)
    const isLoadingState = isLoading || buyerLoading;

    // Handle explicit contexts first
    if (context === 'buyer' || context === 'public') {
      return "/buyer";
    } else if (context === 'seller') {
      return "/seller";
    } else if (user && !isOnAuthPage && !isLoadingState) {
      // User is authenticated and NOT on auth pages and NOT in loading state - route based on profile type
      if (buyerProfile && !profile) {
        // User has buyer profile only
        return "/buyer/dashboard";
      } else if (profile && !buyerProfile) {
        // User has seller profile only
        return "/seller/dashboard";
      } else if (buyerProfile && profile) {
        // User has both profiles - route based on current context
        if (location.pathname.startsWith('/buyer')) {
          return "/buyer/dashboard";
        } else {
          return "/seller/dashboard";
        }
      } else {
        // User is authenticated but no profiles loaded yet - stay on current route context
        if (location.pathname.startsWith('/buyer')) {
          return "/buyer";
        } else {
          return "/seller";
        }
      }
    } else {
      // User is not authenticated OR on auth pages OR in loading state - determine based on context
      if (location.pathname.startsWith('/buyer')) {
        return "/buyer";
      } else if (location.pathname.startsWith('/seller')) {
        return "/seller";
      } else {
        return "/";
      }
    }
  }, [context, user, profile, buyerProfile, location.pathname, isLoading, buyerLoading]);

  const logoElement = (
    <h1 className={`font-bold ${sizeClasses[size]} text-godyspo-gold ${className}`}>
      Go<span className="text-white">Dyspo</span>
    </h1>
  );

  if (disableLink) {
    return <div className="flex items-center">{logoElement}</div>;
  }

  return (
    <Link to={linkDestination} className="flex items-center">
      {logoElement}
    </Link>
  );
});

Logo.displayName = 'Logo';

export default Logo;
